import {
  Badge,
  Box,
  Collapse,
  Flex,
  Icon,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import EpisodeCardWithModals from 'components/card/EpisodeCardWithModals';
import EpisodeSoapSection from 'components/layouts/patients/EpisodeSoapSection';
import { Calendar } from 'lucide-react';
import React from 'react';
import { MdChevronRight, MdLocalHospital } from 'react-icons/md';
import { ClinicalEpisode, EstadoEpisodio } from 'types/models';

// ─── Config ───────────────────────────────────────────────────────────────────

const ESTADO_META: Record<EstadoEpisodio, { label: string; colorScheme: string; dot: string }> = {
  [EstadoEpisodio.ABIERTO]:        { label: 'Abierto',        colorScheme: 'blue',   dot: '#63B3ED' },
  [EstadoEpisodio.EN_TRATAMIENTO]: { label: 'En Tratamiento', colorScheme: 'orange', dot: '#F6AD55' },
  [EstadoEpisodio.CERRADO]:        { label: 'Cerrado',        colorScheme: 'green',  dot: '#68D391' },
  [EstadoEpisodio.ARCHIVADO]:      { label: 'Archivado',      colorScheme: 'gray',   dot: '#A0AEC0' },
};

const CIRCLE_SIZE = '40px';

function formatFecha(d: string): string {
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TimeLineProps {
  episodes: ClinicalEpisode[];
  isExpanded: (ep: ClinicalEpisode) => boolean;
  toggleExpand: (ep: ClinicalEpisode) => void;
  canWrite: boolean;
  isAdmin: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function TimeLine({
  episodes,
  isExpanded,
  toggleExpand,
  canWrite,
  isAdmin,
}: TimeLineProps) {
  const cardBg       = useColorModeValue('gray.50', 'navy.750');
  const cardBorder   = useColorModeValue('gray.100', 'whiteAlpha.100');
  const hoverBg      = useColorModeValue('gray.50', 'navy.700');
  const activeBg     = useColorModeValue('brand.50', 'navy.700');
  const bgPage       = useColorModeValue('white', 'navy.800');
  const textColor    = useColorModeValue('secondaryGray.900', 'white');
  const mutedColor   = useColorModeValue('secondaryGray.500', 'secondaryGray.400');
  const lineColor    = useColorModeValue('gray.200', 'whiteAlpha.200');
  const dividerColor = useColorModeValue('gray.100', 'whiteAlpha.100');

  if (episodes.length === 0) return null;

  return (
    <Box>
      {episodes.map((ep, idx) => {
        const meta     = ESTADO_META[ep.estado];
        const expanded = isExpanded(ep);
        const isLast   = idx === episodes.length - 1;
        const isActive =
          ep.estado === EstadoEpisodio.ABIERTO ||
          ep.estado === EstadoEpisodio.EN_TRATAMIENTO;

        return (
          <Flex key={ep.id} align='stretch' gap='16px'>

            {/* ── Left column: circle icon + connector line ─────────────── */}
            <Flex direction='column' align='center' w={CIRCLE_SIZE} flexShrink={0}>

              {/* Circle with calendar icon */}
              <Flex
                w={CIRCLE_SIZE}
                h={CIRCLE_SIZE}
                borderRadius='full'
                bg={meta.dot}
                align='center'
                justify='center'
                flexShrink={0}
                boxShadow={`0 0 0 4px ${bgPage}`}
                transition='transform 0.15s, box-shadow 0.15s'
                _hover={{ transform: 'scale(1.08)' }}
                zIndex={1}>
                <Calendar size={17} color='white' strokeWidth={2.2} />
              </Flex>

              {/* Connector line — grows to fill height between circles */}
              {!isLast && (
                <Box
                  w='2px'
                  flex={1}
                  bg={lineColor}
                  mt='3px'
                  mb='3px'
                  borderRadius='full'
                />
              )}
            </Flex>

            {/* ── Right column: date row + collapsible card ─────────────── */}
            <Box flex={1} minW={0} pb={isLast ? 0 : '16px'}>

              {/* Date row — same height as circle so they align */}
              <Flex align='center' h={CIRCLE_SIZE} gap='8px' mb='8px'>
                <Text fontSize='sm' fontWeight='700' color={textColor}>
                  {formatFecha(ep.fechaApertura)}
                </Text>
                {ep.fechaCierre && (
                  <Text fontSize='xs' color={mutedColor}>
                    → {formatFecha(ep.fechaCierre)}
                  </Text>
                )}
                <Badge
                  colorScheme={meta.colorScheme}
                  variant={isActive ? 'solid' : 'subtle'}
                  borderRadius='full'
                  px='8px'
                  py='1px'
                  fontSize='10px'
                  fontWeight='700'>
                  {meta.label}
                </Badge>
                <Text fontSize='11px' fontWeight='600' color={mutedColor} letterSpacing='0.03em'>
                  {ep.codigoHc}
                </Text>
              </Flex>

              {/* Episode card */}
              <Box
                border='1px solid'
                borderColor={cardBorder}
                borderLeft='3px solid'
                borderLeftColor={meta.dot}
                borderRadius='14px'
                overflow='hidden'>

                {/* Collapsed header — click to toggle */}
                <Flex
                  align='center'
                  gap='10px'
                  px='14px'
                  py='12px'
                  cursor='pointer'
                  bg={expanded ? activeBg : bgPage}
                  _hover={{ bg: hoverBg }}
                  transition='background 0.15s'
                  onClick={() => toggleExpand(ep)}
                  role='button'
                  aria-expanded={expanded}
                  overflow='hidden'>

                  {/* Motivo */}
                  <Text
                    fontSize='sm'
                    fontWeight='600'
                    color={textColor}
                    noOfLines={1}
                    flex={1}
                    minW={0}>
                    {ep.motivoConsulta}
                  </Text>

                  {/* Diagnóstico chip */}
                  {ep.diagnosticoPrincipal && (
                    <Flex
                      align='center'
                      gap='4px'
                      flexShrink={0}
                      maxW='200px'
                      display={{ base: 'none', md: 'flex' }}>
                      <Icon as={MdLocalHospital} w='12px' h='12px' color={mutedColor} />
                      <Text fontSize='11px' color={mutedColor} noOfLines={1} fontStyle='italic'>
                        {ep.codigoCie10 && (
                          <Box as='span' fontWeight='700' fontStyle='normal' mr='2px'>
                            {ep.codigoCie10}
                          </Box>
                        )}
                        {ep.diagnosticoPrincipal}
                      </Text>
                    </Flex>
                  )}

                  {/* Chevron */}
                  <Icon
                    as={MdChevronRight}
                    w='18px'
                    h='18px'
                    color={mutedColor}
                    flexShrink={0}
                    transform={expanded ? 'rotate(90deg)' : 'rotate(0deg)'}
                    transition='transform 0.2s ease'
                  />
                </Flex>

                {/* Expanded body */}
                <Collapse in={expanded} animateOpacity>
                  <Box borderTop='1px solid' borderColor={dividerColor} bg={cardBg}>
                    <EpisodeCardWithModals
                      episode={ep}
                      canWrite={canWrite}
                      isAdmin={isAdmin}
                      headerless
                      noBorder
                      w='100%'
                    />
                    <Box borderTop='1px solid' borderColor={dividerColor}>
                      <EpisodeSoapSection
                        episode={ep}
                        canWrite={canWrite}
                        isAdmin={isAdmin}
                        noBorder
                      />
                    </Box>
                  </Box>
                </Collapse>
              </Box>
            </Box>
          </Flex>
        );
      })}
    </Box>
  );
}
